export const isPercentage = (val: string) => typeof val === 'string' && val.indexOf('%') > -1;

/**
 * Pure helper: compute effective max width in px for resize/dimension rules.
 * - Width must never exceed parentWidth.
 * - If rowMaxPx is provided (e.g. 95% of current + sibling), cap by it as well.
 * Reusable by Resizer and NodeDimensionSettings.
 */
export const getEffectiveMaxWidthPx = (
  parentWidth: number,
  maxWidth: string | number | undefined,
  rowMaxPx?: number | null,
): number => {
  let effectiveMaxPx: number;
  if (!maxWidth) {
    effectiveMaxPx = parentWidth;
  } else if (isPercentage(String(maxWidth))) {
    const maxPxStr = percentToPx(String(maxWidth), parentWidth);
    effectiveMaxPx = Math.min(parseInt(String(maxPxStr), 10) || parentWidth, parentWidth);
  } else {
    const maxWidthInPx = parseInt(String(maxWidth), 10);
    effectiveMaxPx = Number.isFinite(maxWidthInPx)
      ? Math.min(maxWidthInPx, parentWidth)
      : parentWidth;
  }
  if (rowMaxPx != null && Number.isFinite(rowMaxPx)) {
    effectiveMaxPx = Math.min(effectiveMaxPx, rowMaxPx);
  }
  return Math.round(effectiveMaxPx);
};

export const percentToPx = (value: string, comparativeValue: number) => {
  if (value.indexOf('px') > -1 || value === 'auto' || !comparativeValue) return value;
  const percent = parseInt(value);
  return (percent / 100) * comparativeValue + 'px';
};
export const pxToPercent = (value: number, comparativeValue: number) => {
  const val = (Math.abs(value) / comparativeValue) * 100;
  if (value < 0) return -1 * val;
  else return val;
};

/**
 * Get the dimensions of an element excluding padding and border.
 * @param element - The HTMLElement to get the dimensions from.
 * @returns An object with the width and height of the element, rounded to the nearest integer.
 */
export const getElementDimensionsWithoutPadding = (element: HTMLElement) => {
  const computedStyle = getComputedStyle(element);

  let height = element.clientHeight,
    width = element.clientWidth; // width with padding

  height -= parseFloat(computedStyle.paddingTop) + parseFloat(computedStyle.paddingBottom);
  width -= parseFloat(computedStyle.paddingLeft) + parseFloat(computedStyle.paddingRight);

  return {
    width,
    height,
  };
};

/**
 * Get the dimensions of an element including padding and border.
 * @param element - The HTMLElement to get the dimensions from.
 * @returns An object with the width and height of the element, rounded to the nearest integer.
 */
export const getElementDimensionsWithPadding = (element: HTMLElement) => {
  const height = element.clientHeight,
    width = element.clientWidth; // width with padding excluding border width
  return {
    width,
    height,
  };
};
